import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import GenericDrawer from './GenericComponents/GenericDrawer'
import {
  saveScheduledBackgroundJob,
  BACKGROUND_JOB_TYPES,
  SCHEDULED_BACKGROUND_JOB_KINDS,
} from 'src/redux/scheduledBackgroundJobs'

const buildInitialState = (scheduledBackgroundJob, kind) => ({
  kind: scheduledBackgroundJob?.kind ?? kind,
  name: scheduledBackgroundJob?.name || '',
  backgroundJobType:
    scheduledBackgroundJob?.backgroundJobType
    || BACKGROUND_JOB_TYPES[0].value,
  parameters: scheduledBackgroundJob?.parameters || '',
  cronSchedule: scheduledBackgroundJob?.cronSchedule || '',
})

const ScheduledBackgroundJobDrawer = ({ open, onClose, kind, scheduledBackgroundJob }) => {
  const dispatch = useDispatch()
  const saving = useSelector(state => state.scheduledBackgroundJobs.saving)
  const [form, setForm] = useState(buildInitialState(scheduledBackgroundJob, kind))
  const [error, setError] = useState('')

  const isEdit = Boolean(scheduledBackgroundJob)

  useEffect(() => {
    setForm(buildInitialState(scheduledBackgroundJob, kind))
    setError('')
  }, [scheduledBackgroundJob, kind, open])

  const handleChange = (field) => (event) => {
    setForm(prev => ({ ...prev, [field]: event.target.value }))
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    setError('')

    if (!form.name.trim()) {
      setError('Name is required.')
      return
    }

    const trimmedParameters = form.parameters.trim()
    const trimmedCron = form.cronSchedule.trim()

    if (!trimmedCron) {
      setError('Cron schedule is required.')
      return
    }

    const payload = {
      kind: form.kind,
      name: form.name,
      backgroundJobType: form.backgroundJobType,
      parameters: trimmedParameters === '' ? null : trimmedParameters,
      cronSchedule: trimmedCron,
    }
    dispatch(saveScheduledBackgroundJob(payload))
      .then(() => onClose())
      .catch(() => {})
  }

  return (
    <GenericDrawer
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={isEdit ? 'Edit Scheduled Background Job' : 'New Scheduled Background Job'}
      submitLabel={saving ? 'Saving...' : 'Save'}
      disabled={saving}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FormControl fullWidth disabled>
          <InputLabel id='kind-label'>Kind</InputLabel>
          <Select
            labelId='kind-label'
            label='Kind'
            value={form.kind}
          >
            {SCHEDULED_BACKGROUND_JOB_KINDS.map(kindOption => (
              <MenuItem key={kindOption.value} value={kindOption.value}>
                {kindOption.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label='Name'
          value={form.name}
          onChange={handleChange('name')}
          required
          disabled={isEdit}
          fullWidth
        />

        <FormControl fullWidth disabled={isEdit}>
          <InputLabel id='background-job-type-label'>Type</InputLabel>
          <Select
            labelId='background-job-type-label'
            label='Type'
            value={form.backgroundJobType}
            onChange={handleChange('backgroundJobType')}
          >
            {BACKGROUND_JOB_TYPES.map(type => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label='Parameters (raw JSON)'
          value={form.parameters}
          onChange={handleChange('parameters')}
          multiline
          minRows={4}
          fullWidth
        />

        <TextField
          label='Cron Schedule'
          value={form.cronSchedule}
          onChange={handleChange('cronSchedule')}
          required
          fullWidth
        />

        {error && (
          <Alert severity='error'>{error}</Alert>
        )}
      </Box>
    </GenericDrawer>
  )
}

export default ScheduledBackgroundJobDrawer
